import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../store';
import { requestVerificationCode, clearError } from '../../store/authSlice';
import { isAllowedBetaEmail, MAINTENANCE_CONFIG } from '../../utils/constants';
import './BetaAccessDrawer.css';

const BetaAccessDrawer: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, error } = useAppSelector((state) => state.auth);

  if (MAINTENANCE_CONFIG.ALLOWED_LIST.length === 0) {
    return null;
  }

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    setLocalError(null);
    if (error) {
      dispatch(clearError());
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const normalized = email.trim().toLowerCase();
    if (!normalized) {
      return;
    }

    if (!isAllowedBetaEmail(normalized)) {
      setLocalError('This email is not on the pre-release access list.');
      return;
    }

    setLocalError(null);

    try {
      sessionStorage.setItem('loginEmail', normalized);
      sessionStorage.removeItem('demoMode');
      await dispatch(requestVerificationCode(normalized)).unwrap();
      navigate('/login/verify-code');
    } catch (err) {
      console.error('Failed to send verification code:', err);
      setLocalError(
        typeof err === 'string' ? err : 'Failed to send verification code. Please try again.'
      );
    }
  };

  const displayError = localError || error;

  return (
    <div className="beta-access">
      <div className={`beta-access__shell${isOpen ? ' beta-access__shell--open' : ''}`}>
        <div className={`beta-access__drawer${isOpen ? ' beta-access__drawer--open' : ''}`}>
          <div className="beta-access__panel">
            <p className="beta-access__title">Pre-release access</p>
            <form className="beta-access__form" onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setLocalError(null);
                  if (error) {
                    dispatch(clearError());
                  }
                }}
                placeholder="Enter your email"
                className="beta-access__input"
                disabled={isLoading}
                autoComplete="email"
              />
              {displayError && <p className="beta-access__error">{displayError}</p>}
              {isLoading && <p className="beta-access__loading">Sending code...</p>}
              <button
                type="submit"
                className="beta-access__submit"
                disabled={isLoading || !email.trim()}
              >
                {isLoading ? 'Sending...' : 'Send Code'}
              </button>
            </form>
          </div>
        </div>
        <button
          type="button"
          className="beta-access__toggle"
          onClick={handleToggle}
          aria-label={isOpen ? 'Close pre-release access' : 'Open pre-release access'}
          aria-expanded={isOpen}
        >
          <svg
            className="beta-access__toggle-icon"
            viewBox="0 0 24 24"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M13 2 3 14h8l-1 8 10-12h-8l1-8z" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default BetaAccessDrawer;
